"use client";

import * as React from "react";
import { createPortal } from "react-dom";

/**
 * GlobalTooltip — one styled tooltip for the whole app.
 *
 * Mounts once at the root and listens (via event delegation) for hover/focus
 * on any element that has a `title` attribute. On hover it:
 *   1. reads and **removes** the `title`, so the browser's slow native tooltip
 *      never fires;
 *   2. installs a MutationObserver so that if React re-renders the element and
 *      re-adds the `title` (which is why the native gray box used to slip
 *      back), we immediately re-steal + remove it again;
 *   3. after a short delay shows one styled tooltip in a portal on <body>, so
 *      it can never be clipped by an overflow/scroll container and can flip to
 *      stay on screen;
 *   4. restores the original `title` on mouse-out / blur (keeps it in the DOM
 *      for accessibility when not hovered).
 *
 * Appearance lives in `globals.css` under `.app-tooltip`.
 */

const SHOW_DELAY = 200; // ms — short pause before the tooltip appears

type TipSide = "top" | "bottom" | "left" | "right";
type TipState = { text: string; sub?: string; rect: DOMRect; side: TipSide };

export default function GlobalTooltip() {
  const [tip, setTip] = React.useState<TipState | null>(null);
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(null);

  const tipRef = React.useRef<HTMLDivElement | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeEl = React.useRef<HTMLElement | null>(null);
  // Every element in the hover chain (the hovered element + its ancestors)
  // that carried a `title`. We strip them ALL so a titled ancestor can't fire
  // a second native tooltip, and restore them on mouse-out.
  const strippedRef = React.useRef<{ el: HTMLElement; value: string }[]>([]);
  const observersRef = React.useRef<MutationObserver[]>([]);

  React.useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const stopObserving = () => {
      observersRef.current.forEach((o) => o.disconnect());
      observersRef.current = [];
    };

    // Put every stolen `title` back on the element it came from.
    const restoreTitles = () => {
      strippedRef.current.forEach(({ el, value }) => el.setAttribute("title", value));
      strippedRef.current = [];
      activeEl.current = null;
    };

    const hide = () => {
      clearTimer();
      stopObserving();
      restoreTitles();
      setTip(null);
      setPos(null);
    };

    // Tooltip text comes ONLY from an explicit `title`. We intentionally don't
    // fall back to `aria-label` (screen-reader text) or visible text (noisy /
    // redundant), so a tooltip shows only where a `title` was deliberately set.
    const resolveText = (el: HTMLElement): string | null => {
      const title = el.getAttribute("title");
      return title && title.trim() ? title.trim() : null;
    };

    const enter = (el: HTMLElement) => {
      if (activeEl.current === el) return;
      // Opt-out: anything inside a [data-no-tooltip] subtree (e.g. breadcrumb
      // items, which show a sibling-pages menu instead).
      if (el.closest("[data-no-tooltip]")) return;

      // Rich tooltip: an element may declare a bold title + muted subtitle via
      // data-tooltip-title / data-tooltip-sub (used by the board view tabs).
      const richTitle = el.getAttribute("data-tooltip-title");
      let text: string | null;
      let sub: string | undefined;
      if (richTitle && richTitle.trim()) {
        text = richTitle.trim();
        const s = el.getAttribute("data-tooltip-sub");
        sub = s && s.trim() ? s.trim() : undefined;
      } else {
        text = resolveText(el);
      }
      if (!text) return;

      // Collect the hovered element + every ancestor that has a non-empty title,
      // so a titled ancestor can't fire a second native tooltip.
      const chain: { el: HTMLElement; value: string }[] = [];
      let node: HTMLElement | null = el;
      while (node) {
        const t = node.getAttribute("title");
        if (t != null && t.trim()) chain.push({ el: node, value: t });
        node = node.parentElement;
      }

      // Switching targets — tidy up the previous one first.
      hide();

      activeEl.current = el;
      strippedRef.current = chain;

      chain.forEach(({ el: e }) => {
        e.removeAttribute("title");
        // If React re-renders and re-applies `title`, immediately re-strip it
        // so the native tooltip never gets a chance to appear.
        const observer = new MutationObserver(() => {
          if (e.getAttribute("title") != null) e.removeAttribute("title");
        });
        observer.observe(e, { attributes: true, attributeFilter: ["title"] });
        observersRef.current.push(observer);
      });

      // Optional placement: data-tooltip-side="top|bottom|left|right".
      const sideAttr = el.getAttribute("data-tooltip-side");
      const side: TipSide =
        sideAttr === "top" || sideAttr === "left" || sideAttr === "right" ? sideAttr : "bottom";

      clearTimer();
      timerRef.current = setTimeout(() => {
        if (!activeEl.current) return;
        setPos(null);
        setTip({ text, sub, side, rect: activeEl.current.getBoundingClientRect() });
      }, SHOW_DELAY);
    };

    // Only elements that explicitly opt in (a `title`, or the rich
    // `data-tooltip-title`) get a tooltip. Untitled buttons show nothing.
    const SELECTOR = "[title], [data-tooltip-title]";

    const onOver = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest(SELECTOR) as HTMLElement | null;
      if (el) enter(el);
    };

    const onOut = (e: MouseEvent) => {
      if (!activeEl.current) return;
      const related = e.relatedTarget as Node | null;
      if (related && activeEl.current.contains(related)) return; // moved onto a child
      hide();
    };

    const onFocusIn = (e: FocusEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest(SELECTOR) as HTMLElement | null;
      if (el) enter(el);
    };

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", hide, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide, true);

    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("click", hide, true);
    document.addEventListener("keydown", hide, true);
    // Native drag doesn't fire mouseout, so hide on drag start or the tooltip lingers.
    document.addEventListener("dragstart", hide, true);

    return () => {
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", hide, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide, true);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("click", hide, true);
      document.removeEventListener("keydown", hide, true);
      document.removeEventListener("dragstart", hide, true);
      clearTimer();
      stopObserving();
      restoreTitles();
    };
  }, []);

  // Position precisely once the tooltip has rendered (so we know its size):
  // placed on the requested side, flipped to the opposite side when there's no
  // room, and clamped to the viewport on all sides.
  React.useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const box = tipRef.current.getBoundingClientRect();
    const r = tip.rect;
    const gap = 6;
    const m = 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top: number;
    let left: number;

    if (tip.side === "left" || tip.side === "right") {
      // Vertically centered against the element, placed to the side.
      top = r.top + r.height / 2 - box.height / 2;
      if (tip.side === "right") {
        left = r.right + gap;
        if (left + box.width > vw - m) left = r.left - box.width - gap; // flip left
      } else {
        left = r.left - box.width - gap;
        if (left < m) left = r.right + gap; // flip right
      }
    } else {
      // Horizontally centered against the element, placed above/below.
      left = r.left + r.width / 2 - box.width / 2;
      if (tip.side === "top") {
        top = r.top - box.height - gap;
        if (top < m) top = r.bottom + gap; // flip below
      } else {
        top = r.bottom + gap;
        if (top + box.height > vh - m) top = r.top - box.height - gap; // flip above
      }
    }

    top = Math.max(m, Math.min(top, vh - box.height - m));
    left = Math.max(m, Math.min(left, vw - box.width - m));

    setPos({ left, top });
  }, [tip]);

  if (!tip || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={tipRef}
      className="app-tooltip"
      role="tooltip"
      data-rich={tip.sub ? "true" : undefined}
      style={{
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {tip.sub ? (
        <>
          <span className="app-tooltip__title">{tip.text}</span>
          <span className="app-tooltip__sub">{tip.sub}</span>
        </>
      ) : (
        tip.text
      )}
    </div>,
    document.body,
  );
}
